import {Component, EventEmitter, Input, Output} from '@angular/core';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {FieldFilter, FieldKey, FieldProps, FilterSelector, SearchFilter} from '@twpub/core/models';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {ComponentCategory} from '@twpub/core/enums';
import {FormControl, FormGroupDirective, FormsModule, NgForm} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';

import {MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {MatChip, MatChipRemove, MatChipSet} from '@angular/material/chips';
import {MatError, MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatOptgroup, MatOption, MatSelect} from '@angular/material/select';
import {MatInput} from '@angular/material/input';
import {MatCheckbox} from '@angular/material/checkbox';

@Component({
  selector: 'pub-default-filter',
  templateUrl: './default-filter.component.html',
  styleUrls: ['./default-filter.component.scss'],
  standalone: true,
  imports: [MatIconButton, MatTooltip, MatIcon, MatChipSet, MatChip, MatChipRemove, MatFormField, MatLabel, MatSelect, FormsModule, MatOptgroup, MatOption, MatInput, MatHint, MatError, MatCheckbox]
})
export class DefaultFilterComponent extends BaseConfigurableComponent implements FilterSelector {
  static readonly compName = 'DefaultFilterComponent';

  @Input() searchFilters!: SearchFilter[];
  @Input() fieldFilters!: FieldFilter[];
  @Output() updateFieldFilter = new EventEmitter<FieldFilter[]>();

  hasConceptFields = false;
  hasTermFields = false;
  selectedValues: { [key: string]: string[] | undefined } = {};
  available = true;

  // i18n labels for optgroups
  conceptLevelFieldsLabel = $localize`:@@filter.conceptLevelFields:Concept level fields`;
  termLevelFieldsLabel = $localize`:@@filter.termLevelFields:Term level fields`;

  // i18n labels for UI elements
  noFiltersAvailableLabel = $localize`:@@filter.noFiltersAvailable:No filters are available.`;
  clearFilterTooltip = $localize`:@@filter.clearFilter:Clear filter`;
  fieldLabel = $localize`:@@filter.field:Field`;
  selectFieldLabel = $localize`:@@filter.selectField:Select field`;
  searchForLabel = $localize`:@@filter.searchFor:Search for`;
  pressEnterTooltip = $localize`:@@filter.pressEnter:Press enter to search`;
  searchHintLabel = $localize`:@@filter.searchHint:Enter search terms (matches parts of words). Press enter to search.`;
  minCharactersErrorLabel = $localize`:@@filter.minCharactersError:Minimum 2 characters required, or leave empty to clear filter.`;

  // Search properties
  searchText = '';

  selectedField: SearchFilter | null = null;
  tempSelectedValues: { [key: string]: boolean } = {};
  tempSearchText = '';
  activeFilters: FieldFilter[] = [];

  // Store mapping of merged fields for multi-dictionary support
  private mergedFieldsMap: Map<string, SearchFilter[]> = new Map();

  // Cache merged fields to prevent infinite change detection
  private cachedConceptFields: SearchFilter[] = [];
  private cachedTermFields: SearchFilter[] = [];

  // Cache display filters to prevent chip reloading on hover
  private cachedDisplayFilters: FieldFilter[] = [];
  private lastActiveFiltersHash: string = '';

  matcher = new class implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      // Show error state if there's exactly one character
      return !!control && control.value && control.value.trim().length === 1;
    }
  }();

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [this.VISIBLE_FIELD];

    constructor() {
      super(DefaultFilterComponent, DefaultFilterComponent.compName, 'Default filter', [ComponentCategory.Filter]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<FilterSelector>(DefaultFilterComponent, this.name, initialConfig);
    }
  }

  override doUpdate() {
    super.doUpdate();
    this.hasConceptFields = this.searchFilters.some(filter => filter.fieldKey.fieldLevel === 'CONCEPT');
    this.hasTermFields = this.searchFilters.some(filter => filter.fieldKey.fieldLevel === 'TERM');
    this.available = this.hasConceptFields || this.hasTermFields;
    this.selectedValues = {};
    // Preserve the currently selected field across updates (e.g. after picking a picklist value)
    const previousSelectedField = this.selectedField;
    const previousTempSelectedValues = {...this.tempSelectedValues};
    this.selectedField = null;
    this.activeFilters = [];

    // Build merged fields map
    this.buildMergedFieldsMap();

    // Cache merged fields to prevent recreation on every change detection
    const conceptFilters = this.searchFilters.filter(f => f.fieldKey.fieldLevel === 'CONCEPT');
    this.cachedConceptFields = this.getMergedFields(conceptFilters);

    const termFilters = this.searchFilters.filter(f => f.fieldKey.fieldLevel === 'TERM');
    this.cachedTermFields = this.getMergedFields(termFilters);

    this.fieldFilters.forEach(filter => {
      if (filter.fieldKey.fieldOid && filter.values?.length > 0) {
        this.selectedValues[filter.fieldKey.fieldOid] = [...filter.values];
        this.activeFilters.push(filter);
      }
    });

    // Restore selected field if one was active before update (keeps picklist open after selecting a value)
    if (previousSelectedField) {
      const allCachedFields = [...this.cachedConceptFields, ...this.cachedTermFields];
      const restoredField = allCachedFields.find(
        f => f.fieldKey.label === previousSelectedField.fieldKey.label &&
          f.fieldKey.fieldLevel === previousSelectedField.fieldKey.fieldLevel
      );
      if (restoredField) {
        this.selectedField = restoredField;
        this.tempSelectedValues = previousTempSelectedValues;
      }
    }
  }

  /**
   * Build a map of fields that should be merged (same label and level)
   */
  private buildMergedFieldsMap(): void {
    this.mergedFieldsMap.clear();

    this.searchFilters.forEach(filter => {
      const key = `${filter.fieldKey.fieldLevel}:${filter.fieldKey.label}`;
      if (!this.mergedFieldsMap.has(key)) {
        this.mergedFieldsMap.set(key, []);
      }
      this.mergedFieldsMap.get(key)!.push(filter);
    });
  }

  /**
   * Get all search filters that share the same label and level as the given filter
   */
  private getRelatedFields(filter: SearchFilter): SearchFilter[] {
    const key = `${filter.fieldKey.fieldLevel}:${filter.fieldKey.label}`;
    return this.mergedFieldsMap.get(key) || [filter];
  }

  /**
   * Merge search filters by label and level, combining picklist items
   */
  private getMergedFields(filters: SearchFilter[]): SearchFilter[] {
    const mergedMap = new Map<string, SearchFilter>();

    filters.forEach(filter => {
      const key = `${filter.fieldKey.fieldLevel}:${filter.fieldKey.label}`;

      if (!mergedMap.has(key)) {
        // First occurrence - create merged entry
        mergedMap.set(key, {
          ...filter,
          picklistItems: filter.picklistItems ? [...filter.picklistItems] : undefined
        });
      } else {
        // Validate type compatibility before merging
        const existing = mergedMap.get(key)!;
        const existingIsPicklist = existing.type === 'PICKLIST' || existing.type === 'MULTISELECT';
        const currentIsPicklist = filter.type === 'PICKLIST' || filter.type === 'MULTISELECT';

        if (existingIsPicklist !== currentIsPicklist) {
          this.logger.error(`Field type mismatch: Cannot merge fields "${filter.fieldKey.label}" ("${filter.fieldKey.fieldLevel}").`);
          return;
        }

        // Merge picklist items if they exist
        if (filter.picklistItems && existing.picklistItems) {
          // Merge picklist items, avoiding duplicates by value
          const existingValues = new Set(existing.picklistItems.map(item => item.value));
          filter.picklistItems.forEach(item => {
            if (!existingValues.has(item.value)) {
              existing.picklistItems!.push(item);
              existingValues.add(item.value);
            }
          });
        }
      }
    });

    return Array.from(mergedMap.values());
  }

  toggleFilterValue(fieldKey: FieldKey, value: string, checked: boolean): void {
    let filterValues = this.fieldFilters.find(f => f.fieldKey.fieldOid === fieldKey.fieldOid);

    if (!filterValues) {
      filterValues = {fieldKey, values: []};
      this.fieldFilters.push(filterValues);
    }

    if (checked) {
      filterValues.values.push(value);
    } else {
      filterValues.values = filterValues.values.filter(v => v !== value);
    }
    this.selectedValues[fieldKey.fieldOid] = [...filterValues.values];
    this.updateFieldFilter.emit([filterValues]);
    this.updateActiveFilters();
  }

  selectField(field: SearchFilter): void {
    this.selectedField = field;
    this.tempSelectedValues = {};
    this.tempSearchText = '';

    // Get all related fields to properly initialize temp state
    const relatedFields = this.getRelatedFields(field);

    // Initialize temp state based on existing selection
    if (field.picklistItems && field.fieldKey.fieldOid) {
      // For merged fields, we need to collect all existing values from all related fields
      const allExistingValues = new Set<string>();
      relatedFields.forEach(relatedField => {
        if (relatedField.fieldKey.fieldOid) {
          const existingValues = this.selectedValues[relatedField.fieldKey.fieldOid] || [];
          existingValues.forEach(v => allExistingValues.add(v));
        }
      });

      // Initialize checkboxes - show all picklist items from merged field
      field.picklistItems.forEach(item => {
        this.tempSelectedValues[item.value] = allExistingValues.has(item.value);
      });
    } else if (field.fieldKey.fieldOid) {
      // For text fields, just use the first related field's value
      const existingFilter = this.fieldFilters.find(f => f.fieldKey.fieldOid === field.fieldKey.fieldOid);
      if (existingFilter && existingFilter.values.length > 0) {
        this.tempSearchText = existingFilter.values[0];
      }
    }
  }

  getConceptFields(): SearchFilter[] {
    return this.cachedConceptFields;
  }

  getTermFields(): SearchFilter[] {
    return this.cachedTermFields;
  }

  updatePicklistFilter(): void {
    if (!this.selectedField || !this.selectedField.fieldKey.fieldOid) return;

    // Get all currently selected values
    const selectedValues = Object.entries(this.tempSelectedValues)
      .filter(([_, selected]) => selected)
      .map(([value]) => value);

    // Get all related fields (fields with same label and level)
    const relatedFields = this.getRelatedFields(this.selectedField);

    // Collect all filters to emit at once
    const filtersToEmit: FieldFilter[] = [];

    // Create or update filters for all related fields
    relatedFields.forEach(relatedField => {
      const filterValues: FieldFilter = {
        fieldKey: relatedField.fieldKey,
        values: selectedValues,
        dictionaryId: relatedField.dictionaryId
      };

      // Update state
      const existingIndex = this.fieldFilters.findIndex(
        f => f.fieldKey.fieldOid === filterValues.fieldKey.fieldOid
      );

      if (existingIndex >= 0) {
        this.fieldFilters[existingIndex] = filterValues;
      } else {
        this.fieldFilters.push(filterValues);
      }

      if (filterValues.values.length > 0 && filterValues.fieldKey.fieldOid) {
        this.selectedValues[filterValues.fieldKey.fieldOid] = [...filterValues.values];
      }

      filtersToEmit.push(filterValues);
    });

    // Update active filters once
    this.updateActiveFilters();

    // Emit all filters at once
    if (filtersToEmit.length > 0) {
      this.updateFieldFilter.emit(filtersToEmit);
    }
  }

  updateTextFilter(): void {
    if (!this.selectedField || !this.selectedField.fieldKey.fieldOid) return;

    const trimmedText = this.tempSearchText.trim();

    // Get all related fields (fields with same label and level)
    const relatedFields = this.getRelatedFields(this.selectedField);

    // Check if text is empty or too short
    if (trimmedText.length === 0) {
      // Remove filters for all related fields
      relatedFields.forEach(relatedField => {
        const existingFilter = this.fieldFilters.find(
          f => f.fieldKey.fieldOid === relatedField.fieldKey.fieldOid
        );
        if (existingFilter) {
          this.removeFilter(existingFilter);
        }
      });
      return;
    }

    if (trimmedText.length > 1) {
      // Collect all filters to emit at once
      const filtersToEmit: FieldFilter[] = [];

      // Create filters for all related fields
      relatedFields.forEach(relatedField => {
        const filterValues: FieldFilter = {
          fieldKey: relatedField.fieldKey,
          values: [trimmedText],
          dictionaryId: relatedField.dictionaryId
        };

        // Update state
        const existingIndex = this.fieldFilters.findIndex(
          f => f.fieldKey.fieldOid === filterValues.fieldKey.fieldOid
        );

        if (existingIndex >= 0) {
          this.fieldFilters[existingIndex] = filterValues;
        } else {
          this.fieldFilters.push(filterValues);
        }

        if (filterValues.values.length > 0 && filterValues.fieldKey.fieldOid) {
          this.selectedValues[filterValues.fieldKey.fieldOid] = [...filterValues.values];
        }

        filtersToEmit.push(filterValues);
      });

      // Update active filters once
      this.updateActiveFilters();

      // Emit all filters at once
      if (filtersToEmit.length > 0) {
        this.updateFieldFilter.emit(filtersToEmit);
      }

      // Reset field selection after successful filter application
      this.selectedField = null;
      this.tempSearchText = '';
    }
  }

  editFilter(filter: FieldFilter, event: MouseEvent): void {
    // Prevent triggering if the click was on the remove button
    if ((event.target as HTMLElement).closest('.mat-mdc-chip-remove')) {
      return;
    }

    // Find the corresponding SearchFilter
    let matchedSearchFilter = this.searchFilters.find(
      sf => sf.fieldKey.fieldOid === filter.fieldKey.fieldOid
    );

    // If not found by fieldOid, try to find by label and level (for merged filters)
    if (!matchedSearchFilter) {
      matchedSearchFilter = this.searchFilters.find(
        sf => sf.fieldKey.label === filter.fieldKey.label &&
          sf.fieldKey.fieldLevel === filter.fieldKey.fieldLevel
      );
    }

    if (!matchedSearchFilter) return;

    // Find the merged field from the cached fields (these are what appear in the dropdown)
    const mergedFields = matchedSearchFilter.fieldKey.fieldLevel === 'CONCEPT'
      ? this.cachedConceptFields
      : this.cachedTermFields;

    const mergedField = mergedFields.find(
      mf => mf.fieldKey.label === matchedSearchFilter!.fieldKey.label
    );

    if (!mergedField) return;

    // Set selectedField to the merged field so the dropdown shows the selection
    this.selectedField = mergedField;
    this.selectField(mergedField);

    // Pre-populate the values
    if (mergedField.picklistItems) {
      // For picklist fields
      filter.values.forEach(value => {
        this.tempSelectedValues[value] = true;
      });
    } else {
      // For text fields
      this.tempSearchText = filter.values[0] || '';
    }
  }

  removeFilter(filter: FieldFilter): void {
    if (!filter.fieldKey.fieldOid) return;

    // Find the search filter to get related fields
    let searchFilter = this.searchFilters.find(
      sf => sf.fieldKey.fieldOid === filter.fieldKey.fieldOid
    );

    // If not found by fieldOid, it might be a merged display filter
    // Try to find by label and level
    if (!searchFilter) {
      searchFilter = this.searchFilters.find(
        sf => sf.fieldKey.label === filter.fieldKey.label &&
          sf.fieldKey.fieldLevel === filter.fieldKey.fieldLevel
      );
    }

    if (searchFilter) {
      // Get all related fields (same label and level)
      const relatedFields = this.getRelatedFields(searchFilter);

      // Collect all empty filters to emit at once
      const emptyFilters: FieldFilter[] = [];

      // Remove filters for all related fields
      relatedFields.forEach(relatedField => {
        const index = this.fieldFilters.findIndex(
          f => f.fieldKey.fieldOid === relatedField.fieldKey.fieldOid
        );

        if (index >= 0) {
          this.fieldFilters.splice(index, 1);

          if (relatedField.fieldKey.fieldOid) {
            delete this.selectedValues[relatedField.fieldKey.fieldOid];
          }

          const emptyFilter: FieldFilter = {
            fieldKey: relatedField.fieldKey,
            values: [],
            dictionaryId: relatedField.dictionaryId
          };

          emptyFilters.push(emptyFilter);
        }
      });

      // Deselect the field if the removed filter matches the currently selected field
      // Must happen before emit so doUpdate's restore logic doesn't re-open the field
      if (this.selectedField &&
        (this.selectedField.fieldKey.fieldOid === filter.fieldKey.fieldOid ||
          (this.selectedField.fieldKey.label === filter.fieldKey.label &&
            this.selectedField.fieldKey.fieldLevel === filter.fieldKey.fieldLevel))) {
        this.selectedField = null;
        this.tempSelectedValues = {};
        this.tempSearchText = '';
      }

      this.updateActiveFilters();

      // Emit all empty filters at once
      if (emptyFilters.length > 0) {
        this.updateFieldFilter.emit(emptyFilters);
      }
    } else {
      // Fallback to original behavior if search filter not found
      const index = this.fieldFilters.findIndex(
        f => f.fieldKey.fieldOid === filter.fieldKey.fieldOid
      );

      if (index >= 0) {
        this.fieldFilters.splice(index, 1);

        if (filter.values.length > 0 && filter.fieldKey.fieldOid) {
          delete this.selectedValues[filter.fieldKey.fieldOid];
        }

        const emptyFilter: FieldFilter = {
          fieldKey: filter.fieldKey,
          values: [],
          dictionaryId: filter.dictionaryId
        };

        this.updateFieldFilter.emit([emptyFilter]);
        this.updateActiveFilters();
      }
    }
  }

  updateActiveFilters(): void {
    this.activeFilters = this.fieldFilters.filter(filter =>
      (filter.values && filter.values.length > 0)
    );
    // Invalidate cache when active filters change
    this.lastActiveFiltersHash = '';
  }

  /**
   * Get active filters for display, merging filters with the same label into one
   * This prevents showing multiple chips for the same field from different dictionaries
   */
  getActiveFiltersForDisplay(): FieldFilter[] {
    // Create a hash of active filters to detect changes
    const currentHash = this.activeFilters
      .map(f => `${f.fieldKey.fieldOid}:${f.values.join(',')}`)
      .sort()
      .join('|');

    // Return cached result if nothing changed
    if (this.lastActiveFiltersHash === currentHash && this.cachedDisplayFilters.length > 0) {
      return this.cachedDisplayFilters;
    }

    // Update hash and rebuild cache
    this.lastActiveFiltersHash = currentHash;

    const mergedMap = new Map<string, FieldFilter>();

    this.activeFilters.forEach(filter => {
      const searchFilter = this.searchFilters.find(
        sf => sf.fieldKey.fieldOid === filter.fieldKey.fieldOid
      );

      if (!searchFilter) return;

      const key = `${filter.fieldKey.fieldLevel}:${searchFilter.fieldKey.label}`;

      if (!mergedMap.has(key)) {
        // First occurrence - create merged entry
        mergedMap.set(key, {
          fieldKey: {
            ...filter.fieldKey,
            label: searchFilter.fieldKey.label
          },
          values: [...filter.values]
        });
      } else {
        // Merge values from additional dictionaries
        const existing = mergedMap.get(key)!;
        // Add unique values
        filter.values.forEach(value => {
          if (!existing.values.includes(value)) {
            existing.values.push(value);
          }
        });
      }
    });

    this.cachedDisplayFilters = Array.from(mergedMap.values());
    return this.cachedDisplayFilters;
  }

  getFilterDisplayValue(filter: FieldFilter): string {
    if (!filter.values || filter.values.length === 0) {
      return '';
    }

    // For merged filters, we need to find any related search filter by label
    const searchFilter = this.searchFilters.find(
      sf => sf.fieldKey.fieldOid === filter.fieldKey.fieldOid
    );

    // If not found by oid, try to find by label and level (for merged filters)
    let picklistItems = searchFilter?.picklistItems;
    if (!picklistItems) {
      const relatedFilter = this.searchFilters.find(
        sf => sf.fieldKey.label === filter.fieldKey.label &&
          sf.fieldKey.fieldLevel === filter.fieldKey.fieldLevel
      );
      picklistItems = relatedFilter?.picklistItems;
    }

    // If not a picklist field, it's a text search field
    if (!picklistItems) {
      // Truncate long text values
      const value = filter.values[0];
      return `"${value}"`;
    }

    // Handle picklist fields - need to collect names from all related fields
    const allPicklistItems: any[] = [];
    this.searchFilters.forEach(sf => {
      if (sf.fieldKey.label === filter.fieldKey.label &&
        sf.fieldKey.fieldLevel === filter.fieldKey.fieldLevel &&
        sf.picklistItems) {
        allPicklistItems.push(...sf.picklistItems);
      }
    });

    const selectedNames = allPicklistItems
      .filter(item => filter.values.includes(item.value))
      .map(item => item.name);

    // Remove duplicates
    const uniqueNames = Array.from(new Set(selectedNames));

    if (uniqueNames.length > 2) {
      const remainingCount = uniqueNames.length - 2;
      return `${uniqueNames[0]}, ${uniqueNames[1]} ${$localize`:@@filter.andMore:and ${remainingCount} more`}`;
    }

    if (uniqueNames.length > 1) {
      return `${uniqueNames[0]}, ${uniqueNames[1]}`;
    }

    return uniqueNames.join(', ');
  }

  clearAllFilters(): void {
    // Make a copy of the filters to avoid modification during iteration
    const filtersToRemove = [...this.activeFilters];

    // Remove each filter
    filtersToRemove.forEach(filter => {
      this.removeFilter(filter);
    });

    // Reset selection state
    this.selectedField = null;
    this.tempSelectedValues = {};
    this.tempSearchText = '';

    // Make sure the active filters array is empty
    this.activeFilters = [];
  }
}
